// Assignment per item (Masterplan, "Toewijzing per stap of per item"): one or more people on one cast member,
// set, prop, scene or shot inside a step, next to the people on the whole step. The server keeps them on the step
// (`items` of GET /projects/{id}/steps and of every EpisodeStep); this file groups them per item and writes the
// department head's line ("Anna works on sets · Sam on Backlot street"). Pure.
import { DEPARTMENT_LABEL, type Department } from "../departments.ts";
import { listNames } from "./steps.ts";

export type ItemType = "element" | "shot" | "scene";

export interface ItemAssignment {
  /** the assignment's id (to end it) */
  id: string;
  dept: Department;
  userId: string;
  itemType: ItemType;
  itemId: string;
}

/** The API's item assignment on a step. */
export interface ApiItemAssignment {
  id: string;
  user_id: string;
  item_type: ItemType;
  item_id: string;
}

export const itemKey = (type: ItemType, id: string) => `${type}:${id}`;

/** The live item assignments of a list of steps (project steps or episode steps). */
export function itemsFromSteps(
  steps: readonly { department: Department; items?: readonly ApiItemAssignment[] }[],
): ItemAssignment[] {
  return steps.flatMap((s) =>
    (s.items ?? []).map((i) => ({
      id: i.id,
      dept: s.department,
      userId: i.user_id,
      itemType: i.item_type,
      itemId: i.item_id,
    })),
  );
}

/** One department's assignments per item (`element:<id>` → its assignments, in the order the server gave). */
export function groupByItem(
  assignments: readonly ItemAssignment[],
  dept: Department,
): Map<string, ItemAssignment[]> {
  const out = new Map<string, ItemAssignment[]>();
  const seen = new Set<string>();
  for (const a of assignments) {
    if (a.dept !== dept) continue;
    const k = itemKey(a.itemType, a.itemId);
    // the server answers one live row per person and item; a second one would only show the face twice
    if (seen.has(`${k}|${a.userId}`)) continue;
    seen.add(`${k}|${a.userId}`);
    out.set(k, [...(out.get(k) ?? []), a]);
  }
  return out;
}

/** What the toggle in the Assign popover does: add the person, or end their assignment on this item. */
export function toggleOf(
  onItem: readonly ItemAssignment[],
  userId: string,
): { kind: "assign" } | { kind: "end"; id: string } {
  const live = onItem.find((a) => a.userId === userId);
  return live ? { kind: "end", id: live.id } : { kind: "assign" };
}

/** How the head line counts items of a kind when there are more than two ("Sam on 3 sets"). */
const PLURAL: Record<Department, string> = {
  writing: "scripts",
  breakdown: "shots",
  casting: "cast members",
  wardrobe: "costumes",
  sets: "sets",
  props: "props",
  animatic: "shots",
  draft: "shots",
  final: "shots",
  voice: "voices",
  music: "scenes",
  sound: "shots",
  edit: "shots",
};

const capital = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** "Backlot street", "Backlot street and Stage 1", "3 sets"; per kind, scenes before shots. */
function itemsText(dept: Department, items: readonly { type: ItemType; name: string }[]): string {
  const order: ItemType[] = ["scene", "element", "shot"];
  return listNames(
    order.flatMap((t) => {
      const names = items.filter((i) => i.type === t).map((i) => i.name);
      if (names.length === 0) return [];
      if (names.length <= 2) return [listNames(names)];
      const noun = t === "scene" ? "scenes" : t === "shot" ? "shots" : PLURAL[dept];
      return [`${names.length} ${noun}`];
    }),
  );
}

/**
 * The department head's line: who works on the whole step, then who works on single items.
 * "Anna works on sets · Sam on Backlot street", "Sam and Joe work on Backlot street", "You work on 3 sets".
 * People on the whole step are not named again for their items; items that are no longer on the board (an
 * archived set, a shot of another episode) are left out. Null when nobody works on it.
 */
export function crewLine(input: {
  dept: Department;
  /** user ids on the whole step */
  workers: readonly string[];
  assignments: readonly ItemAssignment[];
  /** the name of an item on the board, null when it is not on the board */
  itemName: (type: ItemType, id: string) => string | null;
  /** "you" for the signed-in person */
  nameOf: (userId: string) => string;
  /** segments after the step's own crew, before "+n more" */
  max?: number;
}): string | null {
  const { dept, workers, nameOf } = input;
  const max = input.max ?? 3;
  const whole = new Set(workers);
  // person → the items they work on (named, on the board)
  const per = new Map<string, { type: ItemType; name: string; key: string }[]>();
  for (const a of input.assignments) {
    if (a.dept !== dept || whole.has(a.userId)) continue;
    const name = input.itemName(a.itemType, a.itemId);
    if (!name) continue;
    const key = itemKey(a.itemType, a.itemId);
    const list = per.get(a.userId) ?? [];
    if (!list.some((x) => x.key === key)) list.push({ type: a.itemType, name, key });
    per.set(a.userId, list);
  }
  // people on the very same items share one segment ("Sam and Joe on Backlot street")
  const groups = new Map<string, { people: string[]; items: { type: ItemType; name: string }[] }>();
  for (const [userId, items] of per) {
    const k = items
      .map((i) => i.key)
      .sort()
      .join(",");
    const g = groups.get(k) ?? { people: [], items };
    g.people.push(userId);
    groups.set(k, g);
  }
  const verb = (ids: readonly string[]) => (ids.length === 1 && nameOf(ids[0]!) !== "you" ? "works" : "work");
  const segments: string[] = [];
  if (workers.length)
    segments.push(
      `${listNames(workers.map(nameOf))} ${verb(workers)} on ${DEPARTMENT_LABEL[dept].toLowerCase()}`,
    );
  const itemSegs = [...groups.values()];
  itemSegs.forEach((g, i) => {
    if (i >= max) return;
    const who = listNames(g.people.map(nameOf));
    const what = itemsText(dept, g.items);
    // without crew on the whole step, the first segment carries the verb
    segments.push(segments.length === 0 ? `${who} ${verb(g.people)} on ${what}` : `${who} on ${what}`);
  });
  if (itemSegs.length > max) segments.push(`${itemSegs.length - max} more`);
  return segments.length ? capital(segments.join(" · ")) : null;
}

/** The tip on an item's faces: "Anna works on Backlot street", "You and Sam work on Shot 3". */
export function itemTip(names: readonly string[], itemName: string): string {
  const verb = names.length === 1 && names[0] !== "you" ? "works" : "work";
  return capital(`${listNames(names)} ${verb} on ${itemName}`);
}

/** One item of a step as a noun: "set", "shot", "scene". An element is what the department makes. */
const SINGULAR: Record<Department, string> = {
  writing: "script",
  breakdown: "shot",
  casting: "cast member",
  wardrobe: "costume",
  sets: "set",
  props: "prop",
  animatic: "shot",
  draft: "shot",
  final: "shot",
  voice: "voice",
  music: "scene",
  sound: "shot",
  edit: "shot",
};

/** "2 sets", "1 set", "1 shot and 2 scenes": a count of items by kind, for a department. */
export function itemCountText(dept: Department, types: readonly ItemType[]): string {
  const n = (t: ItemType) => types.filter((x) => x === t).length;
  const part = (count: number, one: string, many: string) =>
    count ? [`${count} ${count === 1 ? one : many}`] : [];
  const noun = SINGULAR[dept];
  const plural = PLURAL[dept];
  return listNames([
    ...part(n("scene"), "scene", "scenes"),
    ...part(n("element"), noun, plural),
    ...part(n("shot"), "shot", "shots"),
  ]);
}

/**
 * People who work on single items of a step but not on the whole step, with what they work on as a count:
 * `{ userId: "dana", text: "2 sets" }` ("Dana on 2 sets"). Each item counts once per person; people on the whole
 * step are left out (they are named already). In the order the server gave.
 */
export function itemOnlyWork(
  dept: Department,
  workers: readonly string[],
  assignments: readonly ItemAssignment[],
): { userId: string; text: string }[] {
  const whole = new Set(workers);
  const per = new Map<string, Map<string, ItemType>>();
  for (const a of assignments) {
    if (a.dept !== dept || whole.has(a.userId)) continue;
    const items = per.get(a.userId) ?? new Map<string, ItemType>();
    items.set(itemKey(a.itemType, a.itemId), a.itemType);
    per.set(a.userId, items);
  }
  return [...per].map(([userId, items]) => ({ userId, text: itemCountText(dept, [...items.values()]) }));
}

/** What one person works on in one step, for the person's own Home: whole step, and single items by name. */
export interface MyStepWork {
  dept: Department;
  /** the whole step is theirs */
  whole: boolean;
  items: { type: ItemType; id: string }[];
}

/** The steps of a production a person works on: as the step's worker, or on single items. Production order of the API. */
export function myStepWork(
  steps: readonly {
    department: Department;
    skipped?: boolean;
    workers: readonly string[];
    items?: readonly ApiItemAssignment[];
  }[],
  userId: string,
): MyStepWork[] {
  const out: MyStepWork[] = [];
  for (const s of steps) {
    if (s.skipped) continue;
    const whole = s.workers.includes(userId);
    const seen = new Set<string>();
    const items: MyStepWork["items"] = [];
    for (const i of s.items ?? []) {
      const k = itemKey(i.item_type, i.item_id);
      if (i.user_id !== userId || seen.has(k)) continue;
      seen.add(k);
      items.push({ type: i.item_type, id: i.item_id });
    }
    if (whole || items.length) out.push({ dept: s.department, whole, items });
  }
  return out;
}

/**
 * The lines on a person's Home for one production: "You work on sets in Night Shift" for a whole step, and
 * "You work on Shot 2 in Night Shift" for single items (named when the name is known, else counted: "2 sets").
 */
export function myWorkLines(
  production: string,
  work: readonly MyStepWork[],
  itemName: (type: ItemType, id: string) => string | null,
): { dept: Department; text: string }[] {
  const lines: { dept: Department; text: string }[] = [];
  const whole = work.filter((w) => w.whole);
  for (const w of whole)
    lines.push({
      dept: w.dept,
      text: `You work on ${DEPARTMENT_LABEL[w.dept].toLowerCase()} in ${production}`,
    });
  for (const w of work) {
    // items of a step the person already works on as a whole are covered by that line
    if (w.whole || w.items.length === 0) continue;
    const named = w.items.map((i) => ({ ...i, name: itemName(i.type, i.id) }));
    const names = named.filter((i) => i.name).map((i) => i.name as string);
    const unnamed = named.filter((i) => !i.name).map((i) => i.type);
    const what =
      names.length <= 2 && unnamed.length === 0
        ? listNames(names)
        : itemCountText(
            w.dept,
            named.map((i) => i.type),
          );
    lines.push({ dept: w.dept, text: `You work on ${what} in ${production}` });
  }
  return lines;
}
